import { useDispatch, useSelector } from "react-redux";
import { constantData } from "../../../utils/helpers/constantData";
import { PLAN_TYPES } from "../../../utils/helpers/subscriptions/plans";
import { getPlanPrices, isDowngrade } from "../../../utils/helpers/subscriptions/utils";
import styles from "./SubscriptionCard.module.css";
import { useEffect, useState } from "react";
import EmailRequiredModal from "../../modal/emailRequiredModal/EmailRequiredModal";
import getCheckoutData from "../../../store/subscriptions/checkout/Checkout";
import { checkoutAction } from "../../../store/subscriptions/checkout/CheckoutSlice";
import { getCardStyles } from "../../../utils/helpers/subscriptions/subscriptionStyles";
import Cookies from 'js-cookie';
import { formatDateByLanguage } from "../../../utils/time";
import getCancelPreviewData from "../../../store/subscriptions/cancel/CancelPreview";
import { cancelPreviewSliceAction } from "../../../store/subscriptions/cancel/CancelPreviewSlice";
import toast from "react-hot-toast";
import ConfirmationModal from "../../modal/confirmationModal/ConfirmationModal";
import Loader from "../../spinner/Loader";
import getCancelData from "../../../store/subscriptions/cancel/Cancel";
import { cancelAction } from "../../../store/subscriptions/cancel/CancelSlice";
import getMySubscriptionData from "../../../store/subscriptions/mySubscription/MySubscription";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import SignInErrorModal from "../../modal/error/signInErrorModal/SignInErrorModal";
import { detectCurrency } from "../../../utils/languages";

const SubscriptionCard = (props) => {
  const { period, features, isPopular, plan, color, t, mySubscriptionData, setSelectedPlan, lang } = props;
  const { subscriptionList, showModal, setShowModal, setErrorMessage, loadingPlan, setLoadingPlan } = props;
  const dispatch = useDispatch();
  const { user, setUser } = useCurrentUser();
  const [previewData, setPreviewData] = useState({});
  const [showSignIn, setShowSignIn] = useState(false);
  const [cancelModalLoading, setCancelModalLoading] = useState(false);
  const checkoutData = useSelector((state) => state.checkout);
  const cancelPreviewData = useSelector((state) => state.cancelPreview);
  const cancelData = useSelector((state) => state.cancel);
  const token = Cookies.get('auth_token');
  const isCurrentPaidPlan = mySubscriptionData?.plan === plan && mySubscriptionData?.plan !== PLAN_TYPES.FREE;
  const isPremiumOwner = plan === PLAN_TYPES.PREMIUM && mySubscriptionData?.plan === plan;
  const isFamilyOwner = plan === PLAN_TYPES.FAMILY && mySubscriptionData?.plan === plan && mySubscriptionData?.isFamilyOwner;
  const isDowngradePlan = isDowngrade(mySubscriptionData?.plan, plan) && plan !== PLAN_TYPES.FREE;
  const detectedCurrency = detectCurrency();

  function handleSubscribe() {
    if (!user?._id) return setShowModal("login")
    if (plan === PLAN_TYPES.FREE) {
      handleCancel()
      return
    }

    setSelectedPlan(plan)
    sessionStorage.setItem("loadingPlan", plan);
    setLoadingPlan(plan);
    const currency = detectCurrency();
    dispatch(getCheckoutData(token, plan, period, currency));
  };

  function getButtonContent() {
    const isLoading = loadingPlan === plan;
    const isCurrentPlan = mySubscriptionData?.plan === plan;

    if (isLoading) {
      return (
        <div className={styles.loadingDots}>
          <div /> <div /> <div />
        </div>
      );
    }

    if (isCurrentPlan && mySubscriptionData?.plan === PLAN_TYPES.FAMILY && !mySubscriptionData?.isFamilyOwner) return t("current_plan")
    if (isCurrentPlan && mySubscriptionData?.plan !== PLAN_TYPES.FREE && mySubscriptionData?.period?.period === 'monthly' && period === 'yearly') return t("extend")
    if (isCurrentPlan && mySubscriptionData?.plan !== PLAN_TYPES.FREE) return null
    if (isCurrentPlan && mySubscriptionData?.plan === PLAN_TYPES.FREE) return null
    if (plan === PLAN_TYPES.FREE) return null

    return `${t("subscribe")} — ${plan === PLAN_TYPES.FAMILY ? t('vinni_plus_family') : t(plan?.toLowerCase())}`;
    
  };

  function handleCancel() {
    if (token) {
      setShowModal("cancelPreview")
      setCancelModalLoading(true)
      dispatch(getCancelPreviewData(token))
    }
  };

  function handleConfirmCancel() {
    if (token) {
      setCancelModalLoading(true)
      dispatch(getCancelData(token))
    }
  };

  useEffect(() => {
    if (cancelPreviewData?.status === "succeeded") {
      dispatch(cancelPreviewSliceAction.cancelPreviewReset());
      setPreviewData(cancelPreviewData.data)
      setCancelModalLoading(false)
    } else if (cancelPreviewData?.status === "failed") {
      setShowModal(null)
      toast.remove()
      toast.error(t("unknown_error"));
      setCancelModalLoading(false)
      dispatch(cancelPreviewSliceAction.cancelPreviewReset());
    }
  }, [dispatch, cancelPreviewData, t, setShowModal])

  useEffect(() => {
    if (cancelData?.status === "succeeded") {
      setShowModal(null)
      setCancelModalLoading(false)
      dispatch(getMySubscriptionData(token));
      setUser((prev) => ({ ...prev, plan: PLAN_TYPES.FREE }));
      dispatch(cancelAction.cancelReset());
    } else if (cancelData?.status === "failed") {
      dispatch(cancelAction.cancelReset());
      toast.remove()
      toast.error(t("unknown_error"));
      setCancelModalLoading(false)
    }
  }, [dispatch, cancelData, t, setShowModal, setUser, token])

  useEffect(() => {
    if (checkoutData?.status === "succeeded") {
      sessionStorage.removeItem("loadingPlan");
      sessionStorage.removeItem("selectedPlan");
      setLoadingPlan(null);
      if (checkoutData?.checkout?.redirectUrl) {
        window.location.href = checkoutData.checkout.redirectUrl;
      } else {
        setShowModal("error");
        setErrorMessage(t("subscription_checkout_failed"))
      }
      dispatch(checkoutAction.checkoutReset());
    } else if (checkoutData?.status === "failed") {
      setErrorMessage(t(checkoutData?.error?.Error) ?? checkoutData?.error?.Error ?? t("subscription_checkout_failed"))
      sessionStorage.removeItem("loadingPlan");
      setLoadingPlan(null);
      setShowModal("error");
      dispatch(checkoutAction.checkoutReset());
    }
  }, [dispatch, setLoadingPlan, checkoutData, setShowModal, setErrorMessage, t])

  const { cardStyle, buttonStyle, buttonClass, cancelButtonStyle, badgeStyle } = getCardStyles(plan, color, styles);
  const prices = getPlanPrices(subscriptionList, plan?.toLowerCase());
  const usdRaw = period === "yearly" ? prices?.yearly : prices?.monthly;
  const usdFormatted = Number(period === "yearly" ? usdRaw / 12 : usdRaw);
  const usdDisplay = Math.floor(usdFormatted * 100) / 100;
  const amdRaw = period === "yearly" ? prices.amdYearly : prices.amdMonthly;
  const amdFormatted = amdRaw != null ? Number(period === "yearly" ? amdRaw / 12 : amdRaw) : null;
  const amdDisplay = amdFormatted != null ? Number(amdFormatted.toFixed(0)) : null;

  function getIsYearlyToMonthlyDowngrade(mySubscriptionData, plan, period) {
    return (
      mySubscriptionData?.plan === plan &&
      mySubscriptionData?.period?.period === 'yearly' &&
      period === 'monthly'
    );
  }

  function getIsCurrentFreePlan(mySubscriptionData, plan) {
    return mySubscriptionData?.plan === plan && mySubscriptionData?.plan === PLAN_TYPES.FREE;
  }

  function getIsSwitchingFromNonOwnedFamilyToFree(mySubscriptionData, plan) {
    return (
      plan === PLAN_TYPES.FREE &&
      mySubscriptionData?.plan === PLAN_TYPES.FAMILY &&
      !mySubscriptionData?.isFamilyOwner
    );
  }

  function getIsCurrentNonOwnedFamilyPlan(mySubscriptionData, plan) {
    return (
      mySubscriptionData?.plan === plan &&
      mySubscriptionData?.plan === PLAN_TYPES.FAMILY &&
      !mySubscriptionData?.isFamilyOwner
    );
  }

  function getIsSubscribeDisabled({ loadingPlan, mySubscriptionData, plan, period, isDowngradePlan }) {
    return (
      !!loadingPlan ||
      getIsCurrentFreePlan(mySubscriptionData, plan) ||
      isDowngradePlan ||
      getIsYearlyToMonthlyDowngrade(mySubscriptionData, plan, period) ||
      getIsSwitchingFromNonOwnedFamilyToFree(mySubscriptionData, plan) ||
      getIsCurrentNonOwnedFamilyPlan(mySubscriptionData, plan)
    );
  }

  const isSubscribeDisabled = getIsSubscribeDisabled({ loadingPlan, mySubscriptionData, plan, period, isDowngradePlan });

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.card} style={cardStyle}>
          <div className={styles.fullColumn}>
            {mySubscriptionData?.plan === plan && <div className={styles.badge} style={badgeStyle}>{t("current_subscription")}</div>}
            {isPopular && mySubscriptionData?.plan !== PLAN_TYPES.FAMILY && <div className={styles.badge} style={{ backgroundColor: color }}>{t("best_choice")}</div>}
            <div className={styles.header}>
              <div className={styles.title} style={{ color }}>{plan === PLAN_TYPES.FAMILY ? t('vinni_plus_family') : t(plan?.toLowerCase())}</div>
            </div>
            <div className={styles.divider} />
            <div>
              {features.map((f, i) => (
                <div key={i} className={styles.featureRow} style={{ animationDelay: `${0.1 + i * 0.07}s` }}>
                  <div className={styles.featureLeft}>
                    <span className={styles.emoji}>{f.icon}</span>
                    <span className={styles.featureText}>{f.text}</span>
                  </div>
                  {f.statusText ? (
                    <span className={styles.statusText}>{f.statusText}</span>
                  ) : (
                    <img src={`/assets/subscriptions/${f.status}.svg`} alt="" className={styles.checkIcon} />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.pricing}>
            <div className={styles.price} style={{ color }}>
              {detectedCurrency === 'AMD' && amdDisplay != null ? (
                <>
                  <span className={styles.accentPrice}>
                    {amdDisplay}{constantData.currency_symbols?.['AMD']}
                  </span>
                  <span className={styles.secondaryPrice}>
                    {' '}({constantData.currency_symbols?.['USD']}{usdDisplay})
                  </span>
                </>
              ) : (
                <span>{constantData.currency_symbols?.['USD']}{usdDisplay}</span>
              )}
            </div>

            {plan !== PLAN_TYPES.FREE && period === "yearly" && (prices.yearly < (prices.monthly * 12)) && (
              <div className={styles.oldPrice}>
                {detectedCurrency === 'AMD' && prices.amdMonthly != null ? (
                  <>
                    <span className={styles.accentPrice}>
                      {prices.amdMonthly}{constantData.currency_symbols?.['AMD']}
                    </span>
                    {` (${prices.monthly}${constantData.currency_symbols?.['USD']})`}
                  </>
                ) : (
                  `${constantData.currency_symbols?.['USD']}${prices.monthly}`
                )}
              </div>
            )}
            <div className={styles.period}>/ {t("month")}</div>
          </div>
          <div className={styles.buttonWrapper}>
            {isCurrentPaidPlan && mySubscriptionData?.expiresDate && (
              <span className={styles.expiryText}>
                {`${t("expires")}: ${formatDateByLanguage(mySubscriptionData?.expiresDate?.date, lang, true)}`}
              </span>
            )}

            {((mySubscriptionData?.plan === plan && mySubscriptionData?.plan === PLAN_TYPES.PREMIUM) || isFamilyOwner) && (
              <span className={styles.infoText}>
                <span className={styles.infoIcon}>i</span>
                {t('auto_renew_info')}
              </span>
            )}

            <div className={styles.buttonRow}>
              {getButtonContent() && (
                <button className={buttonClass} style={buttonStyle} onClick={handleSubscribe} disabled={isSubscribeDisabled}>
                  {getButtonContent()}
                </button>
              )}
              {(isPremiumOwner || isFamilyOwner) && (
                <button className={buttonClass} style={cancelButtonStyle} disabled={loadingPlan ||  mySubscriptionData?.period?.period !== period} color="#ff4d4d" onClick={handleCancel}>
                  {t("cancel")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {showModal === "email" && (
        <EmailRequiredModal onClose={() => setShowModal("")} t={t} />
      )}
      {showModal === "cancelPreview" && (
        <ConfirmationModal
          show={showModal === "cancelPreview"}
          onHide={() => setShowModal(null)}
          logOutHandler={handleConfirmCancel}
          titles={{
            max_title: cancelModalLoading ? <Loader className={styles.customLoaderHeight}/> : `${t("cancel")}`,
            mid_title: cancelModalLoading ? "" : 
            `${t("subscription_cancel_refund_question_start")} ${previewData?.refundAmount}${constantData.currency_symbols?.[`${previewData?.currency}`]} ${t("subscription_cancel_refund_question_end")}`,
          }}
          loading={cancelModalLoading}
        />
      )}
      {(showModal === "login" || showSignIn) && (
        <SignInErrorModal
          country={localStorage.getItem('country')}
          showErrorModal={showModal === "login"}
          setShowErrorModal={() => setShowModal(null)}
          onHide={() => setShowModal(null)}
          showSignIn={showSignIn}
          setShowSignIn={setShowSignIn}
        />
      )}
    </>
  );
};

export default SubscriptionCard;